import { isNumeric } from '../../util/helpers';
import { parseLatLon } from '../../airport/util/parseUtils';
import { BusinessFinderResult } from './business-finder-result.model';

/**
 * Tag-name prefix carried by classification tags (Part 61 / Part 141 / ...). The last `::` segment is
 * shown on the results card as the school's training-type badge(s).
 */
export const FLIGHT_SCHOOL_CLASSIFICATION_PREFIX = 'Flight Training::Classification::';

/**
 * Tag name marking a school approved for VA (GI Bill) benefits.
 */
export const FLIGHT_SCHOOL_VA_APPROVED_TAG = 'Flight Training::VA Approved';

/**
 * A flight-school search result returned by the AirportsAPI `~/flight-schools` endpoint. A flight school
 * is a business carrying any "Flight Training" tag but NOT the "Flight Training::Private" tag (i.e. open to
 * the public), mirroring the iOS app's flight-school classification. Mirrors {@link FlyingClubResult}'s
 * `static parse` convention.
 */
export class FlightSchoolResult extends BusinessFinderResult {

  /**
   * Training-type badges derived from the `Flight Training::Classification::*` tags (e.g. ["Part 141",
   * "Part 61"]). Empty when the school has no classification tags.
   */
  classifications: string[];

  /**
   * True when the school is approved for VA (GI Bill) benefits (Flight Training::VA Approved).
   */
  vaApproved: boolean;

  /**
   * Affiliated college/university name, when the school is college-affiliated. Null otherwise.
   */
  collegeName: string;

  /**
   * Number of full-time flight instructors (CFIs). Null when unknown.
   */
  instructorFull: number;

  /**
   * Number of part-time flight instructors (CFIs). Null when unknown.
   */
  instructorPart: number;

  /**
   * Thumbnail image for the results card (school logo or first photo). Null when the school has no photo.
   */
  thumbnailPhotoPath: string;

  static parse(data: any): FlightSchoolResult {
    const tags: string[] = (data.tags || []).map(d => d);
    const classifications = tags
      .filter(t => t.indexOf(FLIGHT_SCHOOL_CLASSIFICATION_PREFIX) === 0)
      .map(t => t.substring(FLIGHT_SCHOOL_CLASSIFICATION_PREFIX.length));
    return Object.assign(new FlightSchoolResult(), {
      businessId: data.businessId,
      name: data.name || null,
      city: data.city || null,
      stateProvince: data.stateProvince || null,
      latitude: isNumeric(data.latitude) ? +data.latitude : null,
      longitude: isNumeric(data.longitude) ? +data.longitude : null,
      location: (isNumeric(data.latitude) && isNumeric(data.longitude))
        ? parseLatLon(data.latitude, data.longitude) : null,
      phone1: data.phone1 || null,
      webAddress: data.webAddress || null,
      emailAddress: data.emailAddress || null,
      description: data.description || null,
      distance: isNumeric(data.distance) ? +data.distance : null,
      airportIdent: data.airportIdent || null,
      airportName: data.airportName || null,
      tags: tags,
      classifications: classifications,
      vaApproved: tags.indexOf(FLIGHT_SCHOOL_VA_APPROVED_TAG) !== -1,
      collegeName: data.collegeName || null,
      instructorFull: isNumeric(data.instructorFull) ? +data.instructorFull : null,
      instructorPart: isNumeric(data.instructorPart) ? +data.instructorPart : null,
      thumbnailPhotoPath: data.thumbnailPhotoPath || null
    });
  }
}
